import { isBrowser } from '@utils/isBrowser';
import { useCallback, useEffect, useState } from 'react';

import { SELECTED_PRODUCTS_BY_ID_QUERY } from '../apollo/shopify/queries';
import { storefrontClient } from '../apollo/shopify/storefrontClient';
import { DEFAULT_COUNTRY_CODE } from '../constants/locations';
import { ShopifyGenSelectedProductsQuery } from '../types/shopifygen';
import { mapShopifyProductsToProducts } from '../utils/product';

type Result = {
  products: Product[];
  productsRaw: Extract<
    ShopifyGenSelectedProductsQuery['nodes'][number],
    { __typename?: 'Product' }
  >[];
  loading: boolean;
};

export const useProducts = (
  productIds: string[],
  shouldPoll?: boolean,
): Result => {
  const [products, setProducts] = useState<Product[]>([]);
  const [productsRaw, setProductsRaw] = useState<
    Extract<
      ShopifyGenSelectedProductsQuery['nodes'][number],
      { __typename?: 'Product' }
    >[]
  >([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    setLoading(true);

    const { data } =
      await storefrontClient.query<ShopifyGenSelectedProductsQuery>({
        query: SELECTED_PRODUCTS_BY_ID_QUERY,
        variables: {
          country: DEFAULT_COUNTRY_CODE,
          ids: productIds,
        },
        fetchPolicy: 'no-cache',
      });

    const results = data.nodes.filter(
      (node) => node?.__typename === 'Product',
    ) as Extract<
      ShopifyGenSelectedProductsQuery['nodes'][number],
      { __typename?: 'Product' }
    >[];

    const nextItems = productIds
      .map((id) => results.find((node) => node.id === id))
      .filter((result) => result !== undefined);

    // @ts-ignore
    setProducts(mapShopifyProductsToProducts(nextItems));
    setProductsRaw(nextItems);
    setLoading(false);
  }, [productIds.join('_')]);

  useEffect(() => {
    if (productIds.length) fetchItems();
    else setLoading(false);
  }, [productIds.length, productIds.join('_')]);

  useEffect(() => {
    if (!shouldPoll || !isBrowser()) return () => null;

    const timer = setInterval(() => void fetchItems(), 20000); // 20s interval

    return () => clearInterval(timer);
  }, [shouldPoll]);

  return {
    products,
    productsRaw,
    loading,
  };
};
